import { DateTime } from 'luxon'

import type { MarketProgram } from '@/features/bidding/constants'
import { MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'

const DELIVERY_DAY_COLUMN = 'delivery_day_cet'
const BID_TYPE_COLUMN = 'bid_type'
const ACTIVATION_GROUP_COLUMN = 'activation_group_uuid'
const CAPACITY_BID_TYPE = 'CAPACITY'
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const CAPACITY_GATE_ZONE = 'CET'

export type CapacityGateTime = {
  hour: number
  minute: number
}

// D-1 gates in CET: FCR capacity closes at 17:00 (BID-176/BID-177), mFRR capacity at 07:30 (BID-182).
export const FCR_CAPACITY_GATE: CapacityGateTime = { hour: 17, minute: 0 }
export const MFRR_CAPACITY_GATE: CapacityGateTime = { hour: 7, minute: 30 }

type ParsedTsv = {
  header: string[]
  rows: string[][]
}

function parseTsv(tsv: string): ParsedTsv | null {
  const lines = tsv
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim() !== '')
  if (lines.length < 2) {
    return null
  }
  return {
    header: lines[0].split('\t').map((column) => column.trim()),
    rows: lines.slice(1).map((line) => line.split('\t').map((cell) => cell.trim())),
  }
}

function columnValues(parsed: ParsedTsv, columnName: string): string[] | null {
  const index = parsed.header.indexOf(columnName)
  if (index === -1) {
    return null
  }
  return parsed.rows.map((row) => row[index] ?? '')
}

export function getEarliestDeliveryDay(tsv: string): string | null {
  const parsed = parseTsv(tsv)
  if (parsed === null) {
    return null
  }

  const deliveryDays = columnValues(parsed, DELIVERY_DAY_COLUMN)?.filter((value) => ISO_DATE_PATTERN.test(value))
  if (deliveryDays === undefined || deliveryDays === null || deliveryDays.length === 0) {
    return null
  }
  // ISO dates sort lexicographically in chronological order
  return deliveryDays.sort((a, b) => a.localeCompare(b))[0]
}

export function isCapacityTsv(tsv: string): boolean {
  const parsed = parseTsv(tsv)
  if (parsed === null) {
    return false
  }
  const bidTypes = columnValues(parsed, BID_TYPE_COLUMN)
  return bidTypes !== null && bidTypes.length > 0 && bidTypes.every((value) => value === CAPACITY_BID_TYPE)
}

function capacityGateFor(deliveryDay: string, gate: CapacityGateTime): DateTime {
  return DateTime.fromISO(deliveryDay, { zone: CAPACITY_GATE_ZONE })
    .minus({ days: 1 })
    .set({ hour: gate.hour, minute: gate.minute, second: 0, millisecond: 0 })
}

/**
 * Whether the pasted TSV is a capacity TSV whose earliest delivery day is no longer biddable.
 * The pasted content decides, not any UI selection — the endpoint makes no distinction either.
 * Defaults to the FCR gate (the later of the two) when the market cannot be resolved, to avoid
 * over-warning FCR imports.
 */
export function isCapacityTsvWindowClosed(
  tsv: string,
  now: DateTime,
  gate: CapacityGateTime = FCR_CAPACITY_GATE,
): boolean {
  if (!isCapacityTsv(tsv)) {
    return false
  }
  const deliveryDay = getEarliestDeliveryDay(tsv)
  if (deliveryDay === null) {
    return false
  }
  return now >= capacityGateFor(deliveryDay, gate)
}

export type CapacityMarketFamily = 'FCR' | 'MFRR'

export type ResolvedCapacityMarket = {
  family: CapacityMarketFamily
  gate: CapacityGateTime
}

function marketFamilyOf(program: MarketProgram): CapacityMarketFamily | null {
  if (MFRR_MARKET_PROGRAMS.includes(program)) {
    return 'MFRR'
  }
  if (program.startsWith('FCR') || program.startsWith('NORDICS_FCR')) {
    return 'FCR'
  }
  return null
}

/**
 * Resolve which capacity market a pasted TSV belongs to via its activation group uuids — the same
 * signal the backend buckets on. Advisory only: null (not a capacity TSV, no resolvable uuids, or
 * mixed market families) leaves the caller on its fallback; the import itself must never depend
 * on this.
 */
export function resolveCapacityTsvMarket(
  tsv: string,
  marketProgramsByAgUuid: ReadonlyMap<string, MarketProgram>,
): ResolvedCapacityMarket | null {
  if (!isCapacityTsv(tsv)) {
    return null
  }
  const parsed = parseTsv(tsv)
  if (parsed === null) {
    return null
  }
  const uuids = columnValues(parsed, ACTIVATION_GROUP_COLUMN)
  if (uuids === null) {
    return null
  }

  const families = [...new Set(uuids)]
    .map((uuid) => marketProgramsByAgUuid.get(uuid))
    .filter((program): program is MarketProgram => program !== undefined)
    .map((program) => marketFamilyOf(program))
    .filter((family): family is CapacityMarketFamily => family !== null)

  const uniqueFamilies = new Set(families)
  if (uniqueFamilies.size !== 1) {
    return null
  }

  const family = families[0]
  return { family, gate: family === 'MFRR' ? MFRR_CAPACITY_GATE : FCR_CAPACITY_GATE }
}

export type CapacityWindow = {
  deliveryDay: string
  closesAt: DateTime
  isClosed: boolean
  nextDeliveryDay: string
  nextClosesAt: DateTime
}

/**
 * The capacity window relevant right now for a market with a D-1 gate: offers for tomorrow's
 * delivery close today at the gate time. Once closed, the next window (for the day after
 * tomorrow) is reported too.
 */
export function getCapacityWindow(now: DateTime, gate: CapacityGateTime): CapacityWindow {
  const nowInGateZone = now.setZone(CAPACITY_GATE_ZONE)
  const closesAt = nowInGateZone.set({ hour: gate.hour, minute: gate.minute, second: 0, millisecond: 0 })
  return {
    deliveryDay: nowInGateZone.plus({ days: 1 }).toISODate()!,
    closesAt,
    isClosed: nowInGateZone >= closesAt,
    nextDeliveryDay: nowInGateZone.plus({ days: 2 }).toISODate()!,
    nextClosesAt: closesAt.plus({ days: 1 }),
  }
}

export function formatTimeRemaining(now: DateTime, until: DateTime): string {
  const totalMinutes = Math.max(0, Math.ceil(until.diff(now, 'minutes').minutes))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`
}
